import type { RuleValidationTargetType } from '@atlas/types'
import { ApplicableRulesCard, type ApplicableRulesCardProps } from '@atlas/ui-domain'
import { Tab, TabList, TabPanel, Tabs } from '@atlas/ui-primitives'
import { useApplicableRulesValidation } from '@/hooks/useRuleValidation'
import { RuleValidationPanel } from './RuleValidationPanel'

const RULES_TAB = 'rules'
const RESULTS_TAB = 'results'

export interface ApplicableRulesTabsProps
	extends Omit<
		ApplicableRulesCardProps,
		'resultsHref' | 'onViewResults' | 'onValidateRule' | 'validatingRuleIds' | 'onValidateAll' | 'isValidatingAll'
	> {
	targetType: RuleValidationTargetType
	targetId: string
	/** Group rows under the asset they judged - for a target holding many, e.g. a work order's deliverables. */
	groupBySubject?: boolean
}

/**
 * Applicable rules and their recorded validation results, as two tabs of one section.
 *
 * Every surface that offers both used to place them one below the other, or link off to
 * a separate results page - two answers to "what does this content have to satisfy"
 * shown two different ways. This is the ONE composition: wherever a host has a target to
 * validate, its rules and its results sit behind the same two tabs.
 *
 * The validate controls are wired here rather than by each host, so both tabs request
 * against ONE mutation: the rules tab's "Validate all" and the results tab's
 * "Re-validate" ask for the same run, and a host cannot wire one and forget the other.
 */
export function ApplicableRulesTabs({ targetType, targetId, groupBySubject, ...cardProps }: ApplicableRulesTabsProps) {
	const validation = useApplicableRulesValidation(targetType, targetId)

	return (
		<Tabs defaultSelectedKey={RULES_TAB}>
			<TabList variant="underline" className="mb-4" aria-label="Rules">
				<Tab id={RULES_TAB}>Applicable rules</Tab>
				<Tab id={RESULTS_TAB}>Validation results</Tab>
			</TabList>
			<TabPanel id={RULES_TAB}>
				<ApplicableRulesCard
					{...cardProps}
					onValidateRule={validation.onValidateRule}
					validatingRuleIds={validation.validatingRuleIds}
					onValidateAll={validation.onValidateAll}
					isValidatingAll={validation.isValidatingAll}
				/>
			</TabPanel>
			<TabPanel id={RESULTS_TAB}>
				<RuleValidationPanel
					targetType={targetType}
					targetId={targetId}
					title="Validation results"
					groupBySubject={groupBySubject}
				/>
			</TabPanel>
		</Tabs>
	)
}
